import React from 'react';
import { Box, CircularProgress, Stack, Typography, styled } from '@mui/material';
import { ReactComponent as MedicationsIcon } from 'assets/icons/MedicationsTaken.svg';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import {
  DayStatusIndicator,
  DayStatusIndicatorState,
} from 'components/DayStatusIndicator/DayStatusIndicator';
import { WidgetCard } from 'components/WidgetCard/WidgetCard';
import { CompletionStat } from 'services/statsService';
import { parseLocalDateString } from 'utils';

const DAYS_IN_WEEK = 7;

const DaysRow = styled(Stack)(({ theme }) => ({
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-evenly',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
  },
}));

const CenteredState = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(3),
}));

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 7 consecutive days ending on the latest stat (or today when there are none), gaps filled as noActivity.
export const buildMedicationWeek = (
  medicationStats: CompletionStat[],
  today: Date = new Date(),
): CompletionStat[] => {
  const statsByDate = new Map(medicationStats.map((stat) => [stat.date, stat]));
  const latestKey = medicationStats.reduce<string | null>(
    (latest, stat) => (latest == null || stat.date > latest ? stat.date : latest),
    null,
  );
  const endDate = latestKey ? parseLocalDateString(latestKey) : today;

  return Array.from({ length: DAYS_IN_WEEK }, (_, index) => {
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - (DAYS_IN_WEEK - 1 - index));
    const key = toDateKey(date);
    return statsByDate.get(key) ?? { completed: 0, total: 0, date: key };
  });
};

const getDayLabel = (date: string) =>
  parseLocalDateString(date).toLocaleDateString('en-US', { weekday: 'narrow' });

const getMedicationDayState = (
  stat: CompletionStat,
  isCurrentDay: boolean,
): DayStatusIndicatorState => {
  if (stat.total <= 0) return 'noActivity';
  if (stat.completed >= stat.total) return 'completedAll';
  if (stat.completed <= 0) return isCurrentDay ? 'currentDayEmpty' : 'noneCompleted';
  return 'someCompleted';
};

export interface MedicationWidgetViewProps {
  medicationStats: CompletionStat[];
  isLoading?: boolean;
  isError?: boolean;
}

export const MedicationWidgetView = ({
  medicationStats,
  isLoading = false,
  isError = false,
}: MedicationWidgetViewProps) => {
  const { translation } = useTranslationContext();
  const weekStats = buildMedicationWeek(medicationStats);
  const lastIndex = weekStats.length - 1;

  return (
    <WidgetCard icon={<MedicationsIcon />} title={translation('medications_taken')}>
      {isLoading && (
        <CenteredState>
          <CircularProgress aria-label={translation('medications_taken')} />
        </CenteredState>
      )}
      {!isLoading && isError && (
        <CenteredState>
          <Typography variant="footnote" role="alert">
            {translation('data_unable_to_load')}
          </Typography>
        </CenteredState>
      )}
      {!isLoading && !isError && (
        <DaysRow>
          {weekStats.map((stat, index) => {
            const isCurrentDay = index === lastIndex;
            return (
              <DayStatusIndicator
                key={stat.date}
                label={getDayLabel(stat.date)}
                state={getMedicationDayState(stat, isCurrentDay)}
                underlineLabel={isCurrentDay}
                completionData={{ completed: stat.completed, total: stat.total }}
              />
            );
          })}
        </DaysRow>
      )}
    </WidgetCard>
  );
};
